import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { BACKEND_URL } from "../utils/api";

function FormSelector() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForms, setSelectedForms] = useState([]);

  useEffect(() => {
    // Fetch available forms from backend
    fetch(`${BACKEND_URL}/api/forms`)
      .then((res) => res.json())
      .then((data) => {
        setForms(data.forms || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading forms:", err);
        setLoading(false);
      });
  }, []);

  const handleFormToggle = (formNumber) => {
    if (selectedForms.includes(formNumber)) {
      // Unselect
      setSelectedForms((prev) => prev.filter((f) => f !== formNumber));
    } else {
      // Select
      setSelectedForms((prev) => [...prev, formNumber]);
    }
  };

  const handleProceed = () => {
    if (selectedForms.length === 0) {
      alert("Please select at least one form");
      return;
    }

    // Check if any legacy forms selected
    const hasLegacyForms = selectedForms.some((f) =>
      ["1", "3", "5", "28"].includes(f)
    );

    if (hasLegacyForms) {
      alert("Legacy forms (1, 3, 5, 28) cannot be used with multi-form selection. Please use them individually.");
      return;
    }

    // Navigate to unified form page with selected forms
    navigate("/unified-form", { state: { selectedForms } });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-xl text-gray-600">Loading forms...</div>
        </div>
      </>
    );
  }

  // Group forms by category
  const categories = {};
  forms.forEach((form) => {
    const cat = form.category || "Uncategorized";
    if (!categories[cat]) {
      categories[cat] = [];
    }
    categories[cat].push(form);
  });

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-10 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="text-center mb-10">
            <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 mb-4">
              Patent Form Generator
            </h1>
            <p className="text-gray-700 text-xl font-medium">
              Select one or multiple forms to generate your patent documents
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Total {forms.length} forms available
            </p>
          </div>

          {/* Selection Summary */}
          {selectedForms.length > 0 && (
            <div className="mb-8 bg-white rounded-2xl shadow-xl border-2 border-blue-200 p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-bold text-lg">
                    {selectedForms.length} Selected
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedForms.map((formNum) => (
                      <span
                        key={formNum}
                        className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        Form {formNum}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleProceed}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Proceed to Fill Form →
                </button>
              </div>
            </div>
          )}

          {/* Forms by Category */}
          {Object.keys(categories).map((categoryName) => (
            <div key={categoryName} className="mb-12">
              <h2 className="text-3xl font-bold text-blue-900 mb-6 border-b-4 border-blue-300 pb-3">
                {categoryName}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories[categoryName].map((form) => {
                  const isSelected = selectedForms.includes(form.form_number);
                  const isLegacy = ["1", "3", "5", "28"].includes(form.form_number);

                  return (
                    <div
                      key={form.form_number}
                      onClick={() => handleFormToggle(form.form_number)}
                      className={`
                        relative bg-white rounded-2xl shadow-lg hover:shadow-2xl 
                        transition-all duration-300 cursor-pointer p-8 
                        transform hover:-translate-y-2 border-4
                        ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 ring-4 ring-blue-200"
                            : "border-gray-200 hover:border-blue-300"
                        }
                      `}
                    >
                      {/* Checkbox */}
                      <div className="absolute top-4 right-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-6 h-6 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                        />
                      </div>

                      {/* Form Badge */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-xl text-base font-bold shadow-md">
                          Form {form.form_number}
                        </div>
                        {isLegacy && (
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">
                            Legacy
                          </div>
                        )}
                      </div>

                      {/* Form Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight min-h-[60px]">
                        {form.form_title}
                      </h3>

                      {/* Icon */}
                      <div className="text-sm text-gray-600 mt-4 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-blue-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="font-semibold">
                          {isSelected ? "Selected" : "Click to select"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default FormSelector;
